import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAccountIds } from "../../../../auth/accessToken/components/withAccountIds";
import { useAccountId } from "../../../../auth/useAccountId";
import { CORE_API_HOSTNAME } from "../../../../env";
import fulfill from "../../../../fulfill";
import {
    ApprovalsPayment,
    ApprovalsPaymentResponse,
} from "../../../../types/management-auth/generated";

type UsePaymentApprovalsOptions = {
    forceCheck?: boolean;
};

const fetchPaymentApprovals = async (
    payload: {
        accountId: string;
    } & UsePaymentApprovalsOptions,
): Promise<ApprovalsPaymentResponse[]> => {
    return await fulfill.get({
        accountId: payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${payload.accountId}/management/settings/approvals/payments${payload.forceCheck ? "?force_check=true" : ""}`,
        handlers: {
            200: ({ payments }: { payments: ApprovalsPaymentResponse[] }) => {
                return payments;
            },
        },
    });
};

const fetchPostPaymentApproval = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            approval: ApprovalsPayment;
        };
    },
): Promise<ApprovalsPaymentResponse> => {
    return await fulfill.post<ApprovalsPayment>({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/management/settings/approvals/payments?quickr_version=2`,
        json: options.arg.approval,
        handlers: {
            200: (approval: ApprovalsPaymentResponse) => approval,
        },
    });
};

export const usePaymentApprovals = (options?: UsePaymentApprovalsOptions) => {
    const { prodAccountId } = useAccountIds();
    const {
        data: approvals,
        isLoading,
        error: approvalsError,
    } = useSWR(
        () =>
            prodAccountId
                ? {
                      key: `${prodAccountId}/payment-approvals`,
                      accountId: prodAccountId,
                      ...options,
                  }
                : null,
        fetchPaymentApprovals,
        {
            revalidateOnFocus: true,
            revalidateIfStale: true,
        },
    );
    const {
        trigger: postApproval,
        isMutating: isPosting,
        error: postApprovalError,
        data: postApprovalResponse,
    } = useSWRMutation(
        `${prodAccountId}/payment-approvals`,
        fetchPostPaymentApproval,
    );

    return {
        approvals: approvals || [],
        isLoading,
        approvalsError,
        postApproval,
        isPosting,
        postApprovalError,
        posted: !!postApprovalResponse,
    };
};
